import React, { useEffect, useState } from 'react';
import {
  Badge,
  Btn,
  Failed,
  Field,
  Grid,
  Loading,
  Note,
  Rule,
  T,
  TextInput,
  Toggle
} from './ui';

/**
 * Section 6 - the email policy: anti-fatigue cap, segment thresholds, billing lead time.
 *
 * The cap is the one that needs explaining, because its name suggests it limits sending and it does
 * not. It limits how many engagement emails **one fan** receives in a window - not how many people
 * a campaign reaches, and not how many campaigns exist. It bites only where several campaigns would
 * land on the same fan, which is normal for anyone following several teams.
 *
 * The panel says so in as many words. An operator who reads "max 1 per 24 hours" and concludes they
 * can only send one campaign a day would either work around it or stop using segments, and both are
 * worse than the pile-up it exists to prevent.
 *
 * The compliance footer text used to live here too. It is now in Section 8, where the design puts
 * it - one field, one editor, so two panels cannot disagree about it.
 */
const FatigueSettings = ({ settings, onSave, busy, saving, failed, onRetry }) => {
  const [draft, setDraft] = useState(settings);

  useEffect(() => setDraft(settings), [settings]);

  /*
    "Loading policy…" was a permanent state, not a transient one.

    `draft` is seeded from `settings`, which is `null` both before the fetch and after it fails —
    so a failed policy request left this panel reading "Loading policy…" for ever, with no retry
    and nothing to say the request had already come back.
  */
  if (!draft) {
    return failed ? (
      <Failed title="The email policy could not be loaded" onRetry={onRetry} busy={busy}>
        The fatigue cap, segment thresholds and billing lead time are all in this record, so none
        of them can be shown or changed until it loads.
      </Failed>
    ) : (
      <Loading title="Loading policy…" />
    );
  }

  /*
    Bounds, matched to the server's Joi schema.

    `Number(event.target.value || 0)` turned an empty box into `0`, and `min={1}` is an unenforced
    attribute because there is no form submit — so clearing "Max emails per fan" produced a
    submittable zero. The server refuses it (`maxPerWindow` is `min(1).max(50)`), so no engagement
    email was ever actually at risk; what the operator got was a generic six-second toast with no
    indication that the fault was in that field.
  */
  const BOUNDS = {
    'fatigue.maxPerWindow': { min: 1, max: 50, label: 'Max emails per fan' },
    'fatigue.windowHours': { min: 1, max: 720, label: 'Window (hours)' },
    'segmentThresholds.engagedWithinDays': { min: 1, max: 365, label: 'Engaged within (days)' },
    'segmentThresholds.casualWithinDays': { min: 2, max: 730, label: 'Casual within (days)' },
    billingNoticeLeadDays: { min: 1, max: 60, label: 'Billing notice lead (days)' }
  };

  /** Empty is kept as empty, not coerced to zero — so the field can say what is wrong. */
  const number = (path, key) => (event) => {
    const raw = event.target.value;
    const value = raw === '' ? '' : Number(raw);
    setDraft({ ...draft, [path]: { ...draft[path], [key]: value } });
  };

  const outOfRange = Object.entries(BOUNDS)
    .map(([full, bound]) => {
      const [path, key] = full.split('.');
      const value = key ? draft?.[path]?.[key] : draft?.[path];
      if (value === '' || value === undefined || value === null) {
        return `${bound.label} needs a value.`;
      }
      if (!Number.isInteger(value) || value < bound.min || value > bound.max) {
        return `${bound.label} must be a whole number between ${bound.min} and ${bound.max}.`;
      }
      return null;
    })
    .filter(Boolean);

  const invertedThresholds =
    draft.segmentThresholds?.engagedWithinDays >= draft.segmentThresholds?.casualWithinDays;

  const capped = draft.fatigue?.enabled ?? true;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <Grid cols="1fr 1fr" gap="24px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <strong style={{ fontSize: 14.5, color: T.heading }}>Anti-fatigue cap</strong>
            <Note style={{ marginTop: 6 }}>
              Caps how many news and invitation emails one fan receives in a window. It does not
              limit how many fans a campaign reaches, or how many campaigns you can send - it only
              stops the same fan being mailed repeatedly for matching several segments. Security and
              billing email is never capped.
            </Note>
          </div>

          <Grid cols="1fr 1fr" gap="12px">
            <Field label="Max emails per fan">
              <TextInput
                type="number"
                min={1}
                value={draft.fatigue?.maxPerWindow ?? 1}
                onChange={number('fatigue', 'maxPerWindow')}
              />
            </Field>
            <Field label="Per this many hours">
              <TextInput
                type="number"
                min={1}
                value={draft.fatigue?.windowHours ?? 24}
                onChange={number('fatigue', 'windowHours')}
              />
            </Field>
          </Grid>

          <Toggle
            on={capped}
            label="Anti-fatigue cap enabled"
            onChange={(next) =>
              setDraft({ ...draft, fatigue: { ...draft.fatigue, enabled: next } })
            }
          />

          <Note>
            Changes apply to sends evaluated from now on. Raising the cap does not re-send to anyone
            already skipped - the policy that applied at the time is the one that applied.
          </Note>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <strong style={{ fontSize: 14.5, color: T.heading }}>Segment thresholds</strong>
            <Note style={{ marginTop: 6 }}>
              How engaged / casual / dormant are worked out from a fan&apos;s last sign-in. Dormant
              is everything beyond casual, including fans never seen active. These feed the Segment
              column and the segment filters at the top of the page.
            </Note>
          </div>

          <Grid cols="1fr 1fr" gap="12px">
            <Field label="Engaged: active within (days)">
              <TextInput
                type="number"
                min={1}
                value={draft.segmentThresholds?.engagedWithinDays ?? 7}
                onChange={number('segmentThresholds', 'engagedWithinDays')}
              />
            </Field>
            <Field label="Casual: active within (days)">
              <TextInput
                type="number"
                min={2}
                value={draft.segmentThresholds?.casualWithinDays ?? 30}
                onChange={number('segmentThresholds', 'casualWithinDays')}
              />
            </Field>
          </Grid>

          {invertedThresholds ? (
            <Note style={{ color: T.red }}>
              Engaged must be a shorter window than casual, or nothing would ever be dormant.
            </Note>
          ) : null}

          {/*
            Said here, against the fields, rather than left to the server's generic toast. Each
            line names the field and its range, because "invalid input" on a panel of six numbers
            is not something an operator can act on.
          */}
          {outOfRange.map((message) => (
            <Note key={message} style={{ color: T.red }}>
              {message}
            </Note>
          ))}

          <Field
            label="Billing notice lead time (days before renewal)"
            hint="How far ahead of a charge the advance warning goes out."
          >
            <TextInput
              type="number"
              min={1}
              value={draft.billingNoticeLeadDays ?? 7}
              onChange={(event) =>
                setDraft({ ...draft, billingNoticeLeadDays: Number(event.target.value || 7) })
              }
            />
          </Field>
        </div>
      </Grid>

      {/*
        The bounce line is deliberately not a tick. Hard-bounce suppression is implemented and
        waiting on a delivery-reporting transport, and a green tick beside something not yet
        happening is worse than no line at all.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <Rule on={capped}>
          {capped
            ? 'Cap in force for engagement email'
            : 'Cap switched off - every campaign reaches every match'}
        </Rule>
        <Rule>A repeated trigger cannot send twice - enforced by a unique index, not a check</Rule>
        <Rule on={false}>
          Hard-bounced addresses are suppressed automatically - implemented, and active once a
          delivery-reporting provider is switched on
        </Rule>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Btn
          onClick={() =>
            onSave({
              fatigue: draft.fatigue,
              segmentThresholds: draft.segmentThresholds,
              billingNoticeLeadDays: draft.billingNoticeLeadDays
            })
          }
          disabled={busy || invertedThresholds || outOfRange.length > 0}
        >
          {saving ? 'Saving…' : 'Save Policy'}
        </Btn>
        {settings?.persisted ? null : (
          <Badge tone="amber">Defaults in code — never saved on this environment</Badge>
        )}
      </div>
    </div>
  );
};

export default FatigueSettings;
